import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import { store } from '../../app/store';
import { postAPI } from '../../app/api';
import ParticipantCard from './components/ParticipantCard';
import ViolationAlert from './components/ViolationAlert';
import ExamStatistics from './components/ExamStatistics';
import ViolationHistory from './components/ViolationHistory';

export default function LiveMonitoring() {
  const [selectedExam, setSelectedExam] = useState(null);
  const [availableExams, setAvailableExams] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [violations, setViolations] = useState([]);
  const [statistics, setStatistics] = useState({
    total_participants: 0,
    active_participants: 0,
    submitted_participants: 0,
    flagged_participants: 0,
    total_violations: 0
  });
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [filter, setFilter] = useState('all'); // all, active, flagged, submitted
  const [searchTerm, setSearchTerm] = useState('');
  
  const socketRef = useRef(null);
  const store_data = JSON.parse(store());

  useEffect(() => {
    loadAvailableExams();
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const loadAvailableExams = async () => {
    try {
      const response = await postAPI('/exams/list', {
        org_id: store_data.org_id,
        status: 'active', // Only active exams
        user: store_data
      });
      
      if (response.status) {
        setAvailableExams(response.data);
      }
    } catch (error) {
      console.error('Error loading exams:', error);
    }
  };

  const connectToSocket = () => {
    socketRef.current = io(process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001', {
      auth: {
        token: store_data.access_token
      }
    });

    socketRef.current.on('connect', () => {
      setConnectionStatus('connected');
      console.log('Connected to monitoring socket');
    });

    socketRef.current.on('disconnect', () => {
      setConnectionStatus('disconnected');
      setIsMonitoring(false);
      console.log('Disconnected from monitoring socket');
    });

    // Monitoring events
    socketRef.current.on('monitoring-started', (data) => {
      setParticipants(data.participants || []);
      setViolations(data.recent_violations || []);
      setStatistics({
        total_participants: data.total_participants || 0,
        active_participants: data.active_participants || 0,
        submitted_participants: 0,
        flagged_participants: 0,
        total_violations: data.recent_violations?.length || 0
      });
      setIsMonitoring(true);
    });

    socketRef.current.on('participant-joined', (data) => {
      setParticipants(prev => [...prev, {
        user_id: data.user_id,
        user_name: data.user_name,
        joined_at: data.joined_at,
        status: 'active',
        violations: 0,
        answers_submitted: 0
      }]);
      
      setStatistics(prev => ({
        ...prev,
        total_participants: prev.total_participants + 1,
        active_participants: prev.active_participants + 1
      }));
    });

    socketRef.current.on('participant-disconnected', (data) => {
      setParticipants(prev => prev.map(p => 
        p.user_id === data.user_id 
          ? { ...p, status: 'disconnected', disconnected_at: data.disconnected_at }
          : p
      ));
      
      setStatistics(prev => ({
        ...prev,
        active_participants: Math.max(0, prev.active_participants - 1)
      }));
    });

    socketRef.current.on('security-violation', (data) => {
      setViolations(prev => [data, ...prev.slice(0, 49)]); // Keep last 50 violations
      
      setParticipants(prev => prev.map(p => 
        p.user_id === data.user_id 
          ? { 
              ...p, 
              violations: (p.violations || 0) + 1,
              status: (p.violations || 0) >= 4 ? 'flagged' : p.status
            }
          : p
      ));
      
      setStatistics(prev => ({
        ...prev,
        total_violations: prev.total_violations + 1,
        flagged_participants: participants.filter(p => (p.violations || 0) >= 5).length
      }));
    });

    socketRef.current.on('exam-submitted', (data) => {
      setParticipants(prev => prev.map(p => 
        p.user_id === data.user_id 
          ? { 
              ...p, 
              status: 'submitted',
              submission_time: data.submission_time,
              total_answers: data.total_answers
            }
          : p
      ));
      
      setStatistics(prev => ({
        ...prev,
        active_participants: Math.max(0, prev.active_participants - 1),
        submitted_participants: prev.submitted_participants + 1
      }));
    });

    socketRef.current.on('answer-submitted', (data) => {
      setParticipants(prev => prev.map(p => 
        p.user_id === data.user_id 
          ? { ...p, answers_submitted: data.total_answers }
          : p
      ));
    });

    socketRef.current.on('monitoring-error', (data) => {
      console.error('Monitoring error:', data.message);
      alert(`Monitoring Error: ${data.message}`);
    });
  };

  const startMonitoring = () => {
    if (!selectedExam) {
      alert('Please select an exam to monitor');
      return;
    }

    if (!socketRef.current) {
      connectToSocket();
    }

    setTimeout(() => {
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('start-monitoring', {
          exam_id: selectedExam.id,
          org_id: store_data.org_id,
          admin_user_id: store_data.id
        });
      }
    }, 1000);
  };

  const stopMonitoring = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    setIsMonitoring(false);
    setConnectionStatus('disconnected');
    setParticipants([]);
    setViolations([]);
  };

  const getFilteredParticipants = () => {
    let filtered = participants;

    // Apply status filter
    if (filter !== 'all') {
      filtered = filtered.filter(p => p.status === filter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'success',
      'disconnected': 'warning',
      'flagged': 'danger',
      'submitted': 'info'
    };
    return colors[status] || 'secondary';
  };

  const getConnectionBadge = () => {
    const colors = {
      'connected': 'success',
      'connecting': 'warning',
      'disconnected': 'danger'
    };
    
    return (
      <span className={`badge bg-${colors[connectionStatus]} ms-2`}>
        {connectionStatus.toUpperCase()}
      </span>
    );
  };

  const filteredParticipants = getFilteredParticipants();

  return (
    <div className="live-monitoring">
      {/* Header */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">
                Live Exam Monitoring
                {getConnectionBadge()}
              </h4>
              {selectedExam && (
                <small className="text-muted">
                  Monitoring: {selectedExam.exam_title}
                </small>
              )}
            </div>
            <div className="d-flex gap-2">
              {!isMonitoring ? (
                <button type="button" className="btn btn-success" onClick={startMonitoring}>
                  <i className="fas fa-play me-1"></i>
                  Start Monitoring
                </button>
              ) : (
                <button type="button" className="btn btn-danger" onClick={stopMonitoring}>
                  <i className="fas fa-stop me-1"></i>
                  Stop Monitoring
                </button>
              )}
            </div>
          </div>
        </div>
        
        {!isMonitoring && (
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label htmlFor="examSelect" className="form-label">Select Exam to Monitor</label>
                  <select
                    className="form-select"
                    id="examSelect"
                    value={selectedExam?.id || ''}
                    onChange={(e) => {
                      const exam = availableExams.find(ex => ex.id == e.target.value);
                      setSelectedExam(exam);
                    }}
                  >
                    <option value="">Choose an exam...</option>
                    {availableExams.map(exam => (
                      <option key={exam.id} value={exam.id}>
                        {exam.exam_title} - {new Date(exam.exam_date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mt-4">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Select an active exam and click "Start Monitoring" to begin real-time tracking.
                  </small>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {isMonitoring && (
        <>
          {/* Statistics Row */}
          <div className="row mb-4">
            <div className="col-lg-3 col-md-6">
              <ExamStatistics
                title="Total Participants"
                value={statistics.total_participants}
                color="primary"
                icon="users"
              />
            </div>
            <div className="col-lg-3 col-md-6">
              <ExamStatistics
                title="Active Now"
                value={statistics.active_participants}
                color="success"
                icon="user-check"
              />
            </div>
            <div className="col-lg-3 col-md-6">
              <ExamStatistics
                title="Violations"
                value={statistics.total_violations}
                color="danger"
                icon="exclamation-triangle"
              />
            </div>
            <div className="col-lg-3 col-md-6">
              <ExamStatistics
                title="Submitted"
                value={statistics.submitted_participants}
                color="info"
                icon="check-circle"
              />
            </div>
          </div>

          <div className="row">
            {/* Participants List */}
            <div className="col-lg-8">
              <div className="card">
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      Participants ({filteredParticipants.length})
                    </h6>
                    <div className="d-flex gap-2 align-items-center">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search participants..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: '200px' }}
                      />
                      <select
                        className="form-select form-select-sm"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        style={{ width: '150px' }}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="flagged">Flagged</option>
                        <option value="submitted">Submitted</option>
                        <option value="disconnected">Disconnected</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {filteredParticipants.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-muted">No participants found</p>
                    </div>
                  ) : (
                    <div className="row">
                      {filteredParticipants.map(participant => (
                        <div className="col-md-6 mb-3" key={participant.user_id}>
                          <ParticipantCard participant={participant} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Violations Sidebar */}
            <div className="col-lg-4">
              <ViolationHistory violations={violations} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
